import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import {
  samlConfigEdit,
  samlConfigsSet,
  samlFormReset,
  samlSetInitialState,
} from '~/actions/saml';
import { fetchSAMLConfigs } from '~/api/saml';
import { Button } from '~/eds/src/components/Button/Button';
import { Card } from '~/eds/src/components/Card/Card';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Table } from '~/eds/src/modules/Table/Table';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { useAsync } from '~/hooks/useAsync';
import { injectIntl, useIntl } from 'react-intl';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { useRouting } from '~/routing/useRouting';

const options = {
  enableExportXlsx: false,
  enablePageSizeSelect: false,
  enablePagination: false,
  enableManageColumns: false,
  enableSearch: false,
  enableSelectRows: false,
};

function Page({
  saml,
  // actions
  samlConfigsSet,
  samlFormReset,
  samlSetInitialState,
  currentUser,
}) {
  const intl = useIntl();
  const { location, navigate, params } = useRouting();
  const { clientId } = params;

  const { isLoading } = useAsync(
    fetchSAMLConfigs,
    { clientId },
    {
      condition: clientId,
      deps: [clientId],
      successHandler: (response) => {
        samlFormReset();
        samlConfigsSet(response.results);
      },
    },
  );

  const onCreateConfig = () => {
    navigate(`${location.pathname}/form/new`);
  };

  const onEditConfig = (config) => {
    navigate(`${location.pathname}/form/${config.id}`);
  };

  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'SamlConfiguration.navTitle',
        defaultMessage: 'Single Sign-On (SAML)',
        description: 'Navigation title for SAML configuration page',
      }),
      pathname: RoutePathType.AdminConsoleClientSamlConfiguration,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId: currentUser.client,
    },
    intl,
  });

  const rowActions = [
    {
      label: intl.formatMessage({
        id: 'SamlConfiguration.editConfigurationAction',
        defaultMessage: 'Edit configuration',
        description: 'Row action label to edit SAML configuration',
      }),
      onClick: onEditConfig,
    },
  ];
  const data = saml.configs;

  const columns = [
    {
      key: 'name',
      cellType: 'link',
      title: intl.formatMessage({
        id: 'SamlConfiguration.nameColumn',
        defaultMessage: 'Name',
        description: 'Column title for configuration name',
      }),
      mapCellProps: (d) => {
        return {
          text: d.idp_name,
          onClick: () => onEditConfig(d),
        };
      },
    },
    {
      key: 'lastUpdated',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'SamlConfiguration.lastUpdatedColumn',
        defaultMessage: 'Last Updated',
        description: 'Column title for last updated date',
      }),
      mapCellProps: (config) => {
        return {
          datetime: config.date_updated,
          format: 'full_datetime',
        };
      },
      width: 'auto',
    },
  ];

  const renderConfigTables = () => {
    return (
      <Table
        name={intl.formatMessage({
          id: 'SamlConfiguration.tableName',
          defaultMessage: 'SAML',
          description: 'Table name for SAML configurations',
        })}
        columns={columns}
        data={data}
        options={options}
        rowActions={rowActions}
      />
    );
  };

  const renderConfigCreation = () => {
    return (
      <Card mode="bordered">
        <Layout direction="column" align="center" spacing={6}>
          <Text color="text.primary" variant="subtitle">
            {intl.formatMessage({
              id: 'SamlConfiguration.notConfiguredMessage',
              defaultMessage: 'Single Sign-On has not been configured.',
              description: 'Message when SSO is not configured',
            })}
          </Text>
          <Text preset="description">
            {intl.formatMessage({
              id: 'SamlConfiguration.configurationInstructions',
              defaultMessage:
                'Tap on the button below to configure and manage SAML-based Single Sign-On for your organization.',
              description: 'Instructions for configuring SAML SSO',
            })}
          </Text>
          <Button
            id={OnboardingIdType.AddSamlConfiguration}
            text={intl.formatMessage({
              id: 'SamlConfiguration.addConfigurationButton',
              defaultMessage: 'Add Configuration',
              description: 'Button text to add new SAML configuration',
            })}
            variant="primary"
            onClick={onCreateConfig}
          ></Button>
        </Layout>
      </Card>
    );
  };

  useEffect(() => {
    samlSetInitialState();
  }, []);

  return (
    <PageLayout
      title={intl.formatMessage({
        id: 'SamlConfiguration.pageTitle',
        defaultMessage: 'Single Sign-On (SAML)',
        description: 'Page title for SAML configuration',
      })}
      nav={nav}
      loadingContent={{ isLoading }}
    >
      {saml.configs.length === 0
        ? renderConfigCreation()
        : renderConfigTables()}
    </PageLayout>
  );
}

const mapStateToProps = ({ currentUser, client, saml }) => ({
  currentUser,
  client,
  saml,
});

const mapDispatchToProps = (dispatch, { intl }) => ({
  samlConfigsSet: (configs) => dispatch(samlConfigsSet(configs)),
  samlConfigEdit: (data) => dispatch(samlConfigEdit(data)),
  samlFormReset: () => dispatch(samlFormReset()),
  samlSetInitialState: () => dispatch(samlSetInitialState({ intl })),
});

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(Page));
